/**
 * One stage of the pipeline funnel: a label, a proportional bar, a count, and
 * the conversion rate into the next stage.
 *
 * The bar is coloured by stage from the green→navy ramp, so the row's colour
 * says *where in the funnel* rather than *how good* — which is why the count
 * sits beside it in text and the colour is never the only signal (PRD §9.7).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.8, §4.2
 */
import { cn } from '../../lib/cn';

export interface FunnelRowProps {
  /** The stage name. */
  label: React.ReactNode;
  /** How many records sit at this stage. */
  count: number;
  /** The largest count in the funnel, which sets the full bar width. */
  max: number;
  /** Bar colour — a stage ramp token, passed as a CSS colour value. */
  color: string;
  /**
   * Conversion into the next stage, already formatted (e.g. `"38%"`). Omitted
   * on the final stage, which has nothing to convert into.
   */
  conversion?: string | undefined;
  className?: string;
}

export function FunnelRow({
  label,
  count,
  max,
  color,
  conversion,
  className,
}: FunnelRowProps): React.JSX.Element {
  /* Guarded against an all-zero funnel, where every count and the max are 0 and
     the ratio would be NaN. A new deployment has exactly that shape. */
  const pct = max > 0 ? (count / max) * 100 : 0;

  return (
    <div className={cn('flex items-center gap-3 py-2', className)}>
      <span className="text-body w-[140px] flex-none text-xs font-light">{label}</span>
      <span
        className="h-5 min-w-[26px] rounded-full"
        style={{ width: `${String(pct)}%`, backgroundColor: color }}
        aria-hidden="true"
      />
      <span className="font-display text-navy-text w-6 text-[13px] font-extrabold tabular-nums">
        {count}
      </span>
      {conversion !== undefined && (
        <span className="font-display text-body ml-auto text-xs font-extrabold tabular-nums">
          {conversion}
        </span>
      )}
    </div>
  );
}
